import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import SuccessPredictorCacheService from './predictorCache';
import SuccessPredictorService from './service';
import { SuccessPredictorActions } from './slice';
import { IWorkoutSuccessPredictor, IWorkoutSuccessPredictorCache, TSuccessPredictorAxiosResponse } from './types';
import { isCacheInvalid } from './utils';

export default function* successPredictorWatcherSaga() {
  yield all([
    takeEvery(SuccessPredictorActions.initSuccessPredictor, initFeatureSaga),
    takeEvery(SuccessPredictorActions.loadSuccessPredictor, loadSuccessPredictionSaga),
  ]);
}

export function* initFeatureSaga() {
  yield call(SuccessPredictorService.init, ApiService);
  yield call(SuccessPredictorCacheService.init);
  yield put(SuccessPredictorActions.successPredictorReady());
}

export function* loadSuccessPredictionSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  const workoutIds = action.payload;
  // result - resulting data from cache
  // emptyCache - array of ids not found in cache
  const cacheResult = yield call(checkCacheForPredictions, workoutIds);
  let [result] = cacheResult;
  const [, emptyCache] = cacheResult;
  // fetch data from api for non cached ids
  if (emptyCache.length > 0) {
    const fetchedData: IWorkoutSuccessPredictor[] = yield call(loadMissingPredictions, emptyCache);
    result = [...result, ...fetchedData];
  }
  yield put(SuccessPredictorActions.successPredictorResult(result));
}

export function* checkCacheForPredictions(workoutIds: number[]) {
  const result: IWorkoutSuccessPredictor[] = [];
  const emptyCache: number[] = [];
  for (const workoutId of workoutIds) {
    const cacheValue: IWorkoutSuccessPredictorCache | undefined = yield call(
      SuccessPredictorCacheService.get,
      workoutId
    );
    if (isCacheInvalid(cacheValue) || !cacheValue) {
      emptyCache.push(workoutId);
    } else {
      result.push({
        workoutId,
        data: cacheValue.data,
      });
    }
  }
  return [result, emptyCache];
}

export function* loadMissingPredictions(emtpyCache: number[]) {
  const apiResponse: TSuccessPredictorAxiosResponse = yield call(
    SuccessPredictorService.getSuccessPredictions,
    emtpyCache
  );
  const result = apiResponse.data;
  // save to cache asynchronously
  yield fork(saveLoadedPredictionsToCache, result);
  return result;
}

export function* saveLoadedPredictionsToCache(predictions: IWorkoutSuccessPredictor[]) {
  for (const prediction of predictions) {
    try {
      yield call(SuccessPredictorCacheService.set, prediction);
    } catch (error) {
      // ignore cache errors - indexdb might be blocked
      continue;
    }
  }
}
